import type * as THREE from "three";

/**
 * First-build reveal: subsystem groups rise into place with a short
 * stagger (foundation first, exterior last) instead of popping in.
 *
 * Constraints that shaped this implementation:
 * - Materials are shared module-level singletons, so opacity fades
 *   are off the table — the animation only touches group `position.y`
 *   and `visible`, and restores both exactly on finish/cancel.
 * - The viewer runs `frameloop="demand"`, so the loop must call
 *   `invalidate()` every frame while active (per
 *   r3f-demand-mode-lighting-animations note).
 * - Playwright visual baselines screenshot ~750ms after viewer-ready;
 *   the reveal is skipped under automation (`navigator.webdriver`)
 *   and for `prefers-reduced-motion` users.
 */

export interface RevealTimingConfig {
	staggerMs: number;
	durationMs: number;
	dropMeters: number;
}

export const REVEAL_TIMING: RevealTimingConfig = {
	staggerMs: 70,
	durationMs: 340,
	dropMeters: 0.12,
};

export interface RevealGroupEntry {
	object: THREE.Object3D;
	finalVisible: boolean;
	baseY: number;
}

function easeOutCubic(t: number): number {
	const inverted = 1 - t;
	return 1 - inverted * inverted * inverted;
}

/**
 * Progress (0..1, eased) for group `index` at `elapsedMs`.
 * 0 = still hidden below grade, 1 = settled at base position.
 */
export function getRevealProgress(
	elapsedMs: number,
	index: number,
	timing: RevealTimingConfig = REVEAL_TIMING,
): number {
	const local = (elapsedMs - index * timing.staggerMs) / timing.durationMs;
	if (local <= 0) return 0;
	if (local >= 1) return 1;
	return easeOutCubic(local);
}

export function getRevealTotalMs(
	groupCount: number,
	timing: RevealTimingConfig = REVEAL_TIMING,
): number {
	if (groupCount <= 0) return 0;
	return (groupCount - 1) * timing.staggerMs + timing.durationMs;
}

export function shouldPlayRevealAnimation({
	isFirstBuild,
	// webdriver: Playwright visual baselines screenshot 750ms after
	// viewer-ready. jsdom: component tests stub rAF synchronously and
	// assert visibility right after mount — the reveal is cosmetic,
	// so it never runs under either.
	isAutomated = typeof navigator !== "undefined" &&
		(!!navigator.webdriver || /jsdom/i.test(navigator.userAgent ?? "")),
	prefersReducedMotion = typeof window !== "undefined" &&
		typeof window.matchMedia === "function" &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches,
}: {
	isFirstBuild: boolean;
	isAutomated?: boolean;
	prefersReducedMotion?: boolean;
}): boolean {
	return isFirstBuild && !isAutomated && !prefersReducedMotion;
}

function applyRevealPose(
	entry: RevealGroupEntry,
	progress: number,
	timing: RevealTimingConfig,
): void {
	if (!entry.finalVisible) return;
	entry.object.visible = progress > 0;
	entry.object.position.y = entry.baseY - timing.dropMeters * (1 - progress);
}

function settleRevealPose(entry: RevealGroupEntry): void {
	entry.object.visible = entry.finalVisible;
	entry.object.position.y = entry.baseY;
}

/**
 * Runs the reveal via rAF and returns a cancel function. Cancelling
 * (or natural completion) restores every group to its exact final
 * pose, so callers can fire-and-forget but MUST cancel before
 * disposing/rebuilding the groups. The optional completion callback
 * only runs after natural completion, never after cancellation.
 */
export function playRevealAnimation(
	entries: RevealGroupEntry[],
	invalidate: () => void,
	onComplete?: () => void,
): () => void {
	if (entries.length === 0 || typeof requestAnimationFrame !== "function") {
		return () => {};
	}

	const timing = REVEAL_TIMING;
	const totalMs = getRevealTotalMs(entries.length, timing);
	const startedAt = performance.now();
	let frame = 0;
	let done = false;

	for (const entry of entries) {
		applyRevealPose(entry, 0, timing);
	}
	invalidate();

	const finish = (completedNaturally: boolean) => {
		if (done) return;
		done = true;
		cancelAnimationFrame(frame);
		for (const entry of entries) {
			settleRevealPose(entry);
		}
		invalidate();
		if (completedNaturally) onComplete?.();
	};

	const tick = () => {
		if (done) return;
		const elapsed = performance.now() - startedAt;
		if (elapsed >= totalMs) {
			finish(true);
			return;
		}
		entries.forEach((entry, index) => {
			applyRevealPose(entry, getRevealProgress(elapsed, index, timing), timing);
		});
		invalidate();
		frame = requestAnimationFrame(tick);
	};
	frame = requestAnimationFrame(tick);

	return () => finish(false);
}
